import { ChangeDetectorRef, Component, ElementRef, inject, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { IonContent, ModalController } from '@ionic/angular';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AllergenEnum, DishDto, RestaurantSousTypeValues } from '@timechef/core/api';
import { ANIMATION_DURATION } from '@timechef/core/services/animation.service';
import { AccountRepository } from '@timechef/features/account/state';
// import { AccountRepository } from '@timechef/features/account/state';
import { ensureDate } from '@timechef/core/utils';
import { HistoryService } from '@timechef/features/history/state/history.service';
import { RestaurantsRepository, RestaurantsService } from '@timechef/features/restaurants/state';
import { isCartRetrievalEnabled } from '@timechef/features/restaurants/state/restaurant.utils';
import { Menu, Restaurant, SelectableDishFamily, SelectableFormula, SelectableMenu } from '@timechef/features/restaurants/state/restaurants.models';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MenuCategoryEvent } from '../../components/menu-category/menu-category.component';

@UntilDestroy()
@Component({
  selector: 'app-menu-selection-modal',
  templateUrl: './menu-selection-modal.component.html',
  styleUrls: ['./menu-selection-modal.component.scss'],
  standalone: false,
})
export class MenuSelectionModalComponent implements OnInit {
  @Input() restaurantId!: number;
  @Input() menu!: Menu;
  /** Temporaire refonte SCORP:
   * - **oneday** : sans modale, le composant est utilisé comme socle dans le composant ScorpRestaurantDetailComponent
   * - **calendar** : avec modale, le composant est ouvert depuis le service RestaurantsService.openMenuSelectionModal()
   **/
  @Input() mode: 'oneday' | 'calendar' = 'calendar';

  currentScroll = 0;
  menuDate!: Date;
  menuOrderEndDate?: Date;

  @ViewChild('menuContainer', { read: ElementRef }) menuContainerEl?: ElementRef;
  @ViewChild(IonContent) ionContent?: IonContent;

  restaurantsService: RestaurantsService;

  scrollCategoryIdx$ = new BehaviorSubject(0);
  categoriesBreakpoints: number[] = [];
  userAllergens: AllergenEnum[];
  dishFocused?: { familyId: number; dishId: number };

  menuFilters$ = this.restaurantsRepo.menuFilters$;
  currentDishInformation$ = this.restaurantsRepo.currentDishInformation$;
  selectableMenu$?: Observable<SelectableMenu>;
  isCartFilled$ = this.restaurantsRepo.isCartFilled$;
  restaurant!: Restaurant;

  RestaurantSousTypeValues = RestaurantSousTypeValues;
  historyService = inject(HistoryService);

  constructor(
    injector: Injector,
    private restaurantsRepo: RestaurantsRepository,
    private cdRef: ChangeDetectorRef,
    private modalCtrl: ModalController,
  ) {
    this.userAllergens = injector.get(AccountRepository).getAllergens();
    this.restaurantsService = injector.get(RestaurantsService);
  }

  getRestaurantOrdersForToday$() {
    return this.restaurantsRepo.restaurantOrdersByDate$(ensureDate(this.menuDate)).pipe(distinctUntilChanged());
  }

  ngOnInit() {
    this.menuDate = ensureDate(this.menu.date);

    this.restaurant = this.restaurantsRepo.getRestaurantById(this.restaurantId)!;

    this.selectableMenu$ = this.restaurantsRepo.selectableMenu$(this.menu).pipe(
      map((x) => {
        return { ...x, organized: x.order && x.order.length > 0 ? organizeMenu(x.dishFamilies, x.order) : undefined };
      }),
    );
    this.restaurantsRepo.setMenuFilterCurrentDate(this.menuDate);
    this.menuFilters$.subscribe(() => {
      this.computeBreakpoints();
    });

    this.scrollCategoryIdx$.pipe(distinctUntilChanged()).subscribe((idx) => {
      this.restaurantsRepo.setMenuFilterCurrentCategoryByIndex(idx);
    });

    // Si c'est en oneday, on est pas passé par une ouverture de modale, donc on initialise le panier ici
    if (this.mode === 'oneday') {
      this.restaurantsRepo.initCart({
        restaurantId: this.restaurantId,
        date: this.menuDate,
        pickupLimitDate: this.menu.pickupLimitDate,
        service: this.restaurant.orderSettings!.availableServices[0],
        allowCartRetrieval: isCartRetrievalEnabled(this.restaurant),
      });
    }
  }

  async openOrder(orderId: number) {
    const result = await this.historyService.openOrderModal(orderId, { openSingleAsStandalone: true });
    // if order was cancelled we refresh
    if (result === 'cancel') {
      this.restaurantsService.getRestaurantMenu(this.restaurantId, { initMenuFilter: false }).subscribe();
    }
  }

  async openFormulaModal(formula: SelectableFormula, restaurantId: number, date: Date) {
    this.restaurantsService.openFormulaModal(formula, restaurantId, date);
  }

  async openCartModal() {
    if (this.dishFocused) this.dishFocused = undefined;
    this.restaurantsService.openCartModal();
  }

  async addDishToCart(dish: DishDto, familyId: number, dishId: number) {
    this.restaurantsService.addDishToCart(dish);
    this.dishFocused = { familyId, dishId };
  }

  async removeDishFromCart(dish: DishDto) {
    this.restaurantsService.removeDishFromCart(dish.id);
  }

  async focusDish(familyId: number, dishId: number) {
    this.dishFocused = { familyId, dishId };
  }

  isFocused(familyId: number, dishId: number): boolean {
    return this.dishFocused?.familyId === familyId && this.dishFocused?.dishId === dishId;
  }

  formulaCountInCart(formulas: SelectableFormula[]): number {
    return formulas.reduce((total, formula) => total + formula.selectable.amountInCart, 0);
  }

  async onDishCardClick(dish: DishDto) {
    this.restaurantsService.openDishDetailsModal(this.restaurantId, dish, this.menuDate);
  }

  onSelectCategory(ev: MenuCategoryEvent) {
    this.restaurantsRepo.setMenuFilterCurrentCategory(ev.category);
    this.scrollToCategoryByIdx(ev.idx);
  }

  async onOpenOptions() {
    await this.restaurantsService.openSelectDishInformationModal();
    this.cdRef.markForCheck();

    setTimeout(() => {
      this.computeBreakpoints();
    }, 0);
  }

  private scrollToCategoryByIdx = (idx: number) => {
    return this.ionContent?.scrollToPoint(null, this.categoriesBreakpoints[idx], ANIMATION_DURATION);
  };

  onScroll(ev: any) {
    if (this.dishFocused) this.dishFocused = undefined;
    const y = ev.detail.scrollTop;
    this.currentScroll = y;

    if (this.categoriesBreakpoints.length === 0) {
      this.computeBreakpoints();
    }

    // Start from higher breakpoints and stop at the first one that is reached
    for (let i = this.categoriesBreakpoints.length; i >= 0; i--) {
      if (y >= this.categoriesBreakpoints[i]) {
        this.scrollCategoryIdx$.next(i);
        break;
      }
    }
  }

  private computeBreakpoints() {
    const familiesEl = this.menuContainerEl?.nativeElement?.querySelector('.families');
    if (familiesEl) {
      const filtersHeight = this.menuContainerEl?.nativeElement?.querySelector('.filters')?.clientHeight ?? 0;
      const families = familiesEl.querySelectorAll('.family');

      const categories: number[] = [];
      families.forEach((famEl: any) => {
        categories.push(famEl.offsetTop - filtersHeight);
      });
      this.categoriesBreakpoints = categories;
    }
  }

  async dismissModal() {
    await this.modalCtrl.dismiss();
  }

  trackById = (_idx: any, item: { id: any }) => item.id;
  trackByLabel = (_idx: any, item: { label: any }) => item.label;
}

export const organizeMenu = (dishFamilies: SelectableDishFamily[], order: string[]): SelectableDishFamily[] => {
  const dishFamiliesMap = new Map<string, SelectableDishFamily>();

  dishFamilies.forEach((df) => {
    if (df.dishesByComposantesMenus && df.dishesByComposantesMenus.length > 0) {
      df.dishesByComposantesMenus.forEach((subDishFamily) => {
        const key = subDishFamily.label ? `${df.externalRef}-${subDishFamily.label}` : df.externalRef;
        dishFamiliesMap.set(key, {
          externalRef: key,
          dishes: subDishFamily.dishes,
          label: subDishFamily.label || df.label,
          maxItemsAllowed: df.maxItemsAllowed,
          isMaxItemsReached: df.isMaxItemsReached,
          dishesByComposantesMenus: [],
        });
      });
    } else {
      dishFamiliesMap.set(df.externalRef, df);
    }
  });

  // return the dish families in the order specified by the order array and the rest of dishFamilies at the end if not in the order array
  const organizedDishFamilies = order.map((label) => dishFamiliesMap.get(label)).filter((df) => df !== undefined) as SelectableDishFamily[];
  const restOfDishFamilies = Array.from(dishFamiliesMap.values()).filter((df) => !order.includes(df.externalRef));

  return [...organizedDishFamilies, ...restOfDishFamilies];
};
